import * as React from 'react'
import { useCallback } from 'react'
import {
  FileText,
  LayoutGrid,
  Maximize2,
  Minus,
  Plus,
  ZoomIn,
} from 'lucide-react'
import { cn } from '../utils'

export type PreviewMode = 'page' | 'overview' | 'focus'

export interface PreviewControlsProps {
  zoom: number
  onZoomChange: (zoom: number) => void
  mode: PreviewMode
  onModeChange: (mode: PreviewMode) => void
  pageCount?: number
  currentPage?: number
  className?: string
}

const iconButtonStyles = cn(
  'p-2 rounded-lg',
  'border border-transparent',
  'hover:bg-gray-100 hover:border-gray-200',
  'active:bg-gray-200',
  'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-transparent',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
  'transition-all duration-150 ease-out'
)

const presetButtonStyles = (active: boolean) =>
  cn(
    'px-2.5 py-1.5 text-xs font-semibold rounded-md',
    'border',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
    'transition-all duration-150 ease-out',
    active
      ? 'bg-primary-50 text-primary-700 border-primary-200'
      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
  )

export function PreviewControls({
  zoom,
  onZoomChange,
  mode,
  onModeChange,
  pageCount = 1,
  currentPage = 1,
  className,
}: PreviewControlsProps) {
  const handleZoomPreset = useCallback(
    (preset: number) => onZoomChange(preset),
    [onZoomChange]
  )

  const modes: { id: PreviewMode; icon: React.ReactNode; label: string }[] = [
    { id: 'page', icon: <FileText className="size-4" />, label: 'Page' },
    { id: 'overview', icon: <LayoutGrid className="size-4" />, label: 'Overview' },
    { id: 'focus', icon: <Maximize2 className="size-4" />, label: 'Focus' },
  ]

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 px-2 py-2',
        'bg-white/95 backdrop-blur-sm border-b border-gray-200',
        'flex-wrap',
        className
      )}
    >
      {/* View Mode Toggle */}
      <div
        className="flex gap-0.5 p-1 bg-gray-100 rounded-lg shrink-0"
        role="radiogroup"
        aria-label="Preview mode"
      >
        {modes.map((m) => (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={mode === m.id}
            onClick={() => onModeChange(m.id)}
            className={cn(
              'px-2.5 py-1.5 text-sm font-medium rounded-md',
              'flex items-center gap-1.5',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
              'transition-all duration-150 ease-out',
              mode === m.id
                ? 'bg-white shadow-sm text-gray-900'
                : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
            )}
          >
            {m.icon}
            <span className="hidden md:inline">{m.label}</span>
          </button>
        ))}
      </div>

      {/* Zoom Controls */}
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => onZoomChange(Math.max(25, zoom - 25))}
          disabled={zoom <= 25}
          className={iconButtonStyles}
          aria-label="Zoom out"
        >
          <Minus className="size-4" />
        </button>

        <div className="flex items-center gap-1.5 px-1">
          <input
            type="range"
            min={25}
            max={150}
            step={5}
            value={zoom}
            onChange={(e) => onZoomChange(Number(e.target.value))}
            className="w-16 sm:w-20 accent-primary-500"
            aria-label="Zoom level"
          />
          <span className="text-xs font-medium text-gray-600 w-9 text-right tabular-nums">
            {zoom}%
          </span>
        </div>

        <button
          type="button"
          onClick={() => onZoomChange(Math.min(150, zoom + 25))}
          disabled={zoom >= 150}
          className={iconButtonStyles}
          aria-label="Zoom in"
        >
          <Plus className="size-4" />
        </button>
      </div>

      {/* Zoom Presets */}
      <div className="flex gap-1 shrink-0">
        <button
          type="button"
          onClick={() => handleZoomPreset(50)}
          className={presetButtonStyles(zoom === 50)}
        >
          50%
        </button>
        <button
          type="button"
          onClick={() => handleZoomPreset(100)}
          className={presetButtonStyles(zoom === 100)}
        >
          100%
        </button>
        <button
          type="button"
          onClick={() => handleZoomPreset(75)}
          className={cn(presetButtonStyles(zoom === 75), 'flex items-center gap-1')}
          aria-label="Fit to width"
        >
          <ZoomIn className="size-3" />
          <span className="hidden sm:inline">Fit</span>
        </button>
      </div>

      {pageCount > 1 && (
        <span className="ml-auto text-xs font-medium text-gray-500 tabular-nums shrink-0">
          {currentPage}/{pageCount}
        </span>
      )}
    </div>
  )
}
